import React, {useCallback, useMemo} from 'react';
import {useIntl} from "react-intl";

import {activityStore} from "../../../../../store/activity.store";
import {debounce} from "../../../../../store/utils/debounce";
import style from './style.module.css';
import CustomTreeSelect from "../../../../CustomFilterSelect/CustomFilterSelect";

const ActivityTagsFilter = () => {
  const { formatMessage } = useIntl();
  const selectedTags = activityStore(state => state.intervalsParams.tags);
  const filterTags = activityStore(state => state.filterTags);
  const tagsLoading = activityStore(state => state.loading.getTagsStartPage);
  const loadingMore = activityStore(state => state.loading.getNextTagsPage);
  const isLastPage = activityStore(state => state.tagsPaginationInfo.last);
  const setIntervalsParams = activityStore(state => state.setIntervalsParams);
  const setTagsParams = activityStore(state => state.setTagsParams);
  const getTagsStartPage = activityStore(state => state.getTagsStartPage);
  const getNextTagsPage = activityStore(state => state.getNextTagsPage);

  const onTagsSelectChangeHandlerCallback = useCallback(
    (values: string[]) => setIntervalsParams({ tags: values || [] }),
    [setIntervalsParams]
  );

  const onSearchCallback = useCallback(debounce((value: string) => {
    setTagsParams({ query: value });
    getTagsStartPage();
  }, 500), [setTagsParams, getTagsStartPage]);

  const onLoadMore = useCallback(() => {
    if (isLastPage || loadingMore || tagsLoading) return;
    getNextTagsPage();
  }, [isLastPage, loadingMore, tagsLoading, getNextTagsPage]);

  const selectedSet = useMemo(() => new Set(selectedTags || []), [selectedTags]);

  // Selected tags always appear first so they stay visible (and deselectable)
  // even when filtered out by the current server-side search.
  const treeData = useMemo(() => {
    const toNode = (tag: string) => ({ value: tag, label: tag, title: tag, key: tag, name: tag });
    const selected = selectedTags || [];
    const rest = (filterTags || []).filter(tag => !selectedSet.has(tag));
    return [...selected.map(toNode), ...rest.map(toNode)];
  }, [selectedTags, filterTags, selectedSet]);

  // Keep selected tags visible regardless of the local search input.
  const filterTreeNode = useCallback((input: string, node: { value: string; name: string }) => {
    if (selectedSet.has(node.value)) return true;
    return !input || node.name.toLowerCase().includes(input.toLowerCase());
  }, [selectedSet]);

  return (
    <CustomTreeSelect
      allowClear
      showSearch
      className={style.ActivityTagsFilter}
      value={selectedTags}
      onChange={onTagsSelectChangeHandlerCallback}
      treeData={treeData}
      loading={tagsLoading}
      placeholder={formatMessage({ id: 'SELECT_TAGS' })}
      onSearch={onSearchCallback}
      onLoadMore={onLoadMore}
      filterTreeNode={filterTreeNode}
      label={formatMessage({ id: 'TAGS' })}
    />
  )
};

export default ActivityTagsFilter;
